import { adminSettingsService } from '@/services/admin-settings.service';
import { useAdminAuthStore } from '@/store/admin-auth.store';
import { tokenManager } from '@/utils/token-manager';
import type { AdminFilters, FeatureFlag } from '@/types/admin.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useAdminLogin() {
  const { setUser, setTokens } = useAdminAuthStore();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      adminSettingsService.login(email, password),
    onSuccess: (response) => {
      const { user, tokens } = response?.data ?? response;
      if (user.role !== 'super_admin' && user.role !== 'admin') {
        toast.error("Accès refusé : droits administrateur requis");
        return;
      }
      tokenManager.saveTokens(tokens);
      setUser(user);
      setTokens(tokens);
      toast.success('Connexion réussie');
    },
    onError: () => toast.error('Email ou mot de passe incorrect'),
  });
}

export function useAdminProfile() {
  const { setUser } = useAdminAuthStore();
  return useQuery({
    queryKey: ['admin-profile'],
    queryFn: async () => {
      const response = await adminSettingsService.getProfile();
      const user = response?.data ?? response;
      setUser(user);
      return response;
    },
    enabled: !!tokenManager.getTokens()?.accessToken,
    retry: false,
  });
}

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => adminSettingsService.getSettings(),
  });
}

export function useUpdateSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => adminSettingsService.updateSetting(key, value),
    onSuccess: () => { toast.success('Paramètre mis à jour'); qc.invalidateQueries({ queryKey: ['admin-settings'] }); },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['admin-feature-flags'],
    queryFn: () => adminSettingsService.getFeatureFlags(),
  });
}

export function useToggleFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => adminSettingsService.toggleFeatureFlag(key),
    onSuccess: () => { toast.success('Feature flag mis à jour'); qc.invalidateQueries({ queryKey: ['admin-feature-flags'] }); },
    onError: () => toast.error('Erreur'),
  });
}

export function useCreateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FeatureFlag) => adminSettingsService.createFeatureFlag(data),
    onSuccess: () => { toast.success('Feature flag créé'); qc.invalidateQueries({ queryKey: ['admin-feature-flags'] }); },
    onError: () => toast.error('Erreur lors de la création'),
  });
}

export function useAuditLogs(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-audit-logs', filters],
    queryFn: () => adminSettingsService.getAuditLogs(filters),
  });
}
